import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';

// =====================================================
// ULTRA-SMART ENROLLMENT TRANSACTION SERVICE V2
// Revolutionary atomic transaction engine that treats
// enrollment+billing+notifications as single unit
// =====================================================

export interface EnrollmentTransactionRequest {
  student_id: string;
  class_id: string;
  organization_id: string;
  enrolled_by: string;
  
  // Payment configuration
  auto_billing_enabled: boolean;
  billing_amount_cents?: number;
  billing_day?: number;
  
  // Smart orchestration options
  payment_strategy?: 'immediate' | 'deferred' | 'plan';
  max_retry_attempts?: number;
  payment_window_hours?: number;
}

export interface EnrollmentTransaction {
  id: string;
  student_id: string;
  class_id: string;
  organization_id: string;
  enrolled_by: string;
  
  // Transaction state
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'rolled_back' | 'expired';
  created_at: Date;
  updated_at: Date;
  expires_at: Date;
  
  // Payment orchestration
  auto_billing_enabled: boolean;
  billing_amount_cents?: number;
  billing_day?: number;
  payment_strategy: 'immediate' | 'deferred' | 'plan';
  payment_window_hours: number;
  
  // Results tracking
  enrollment_id?: string;
  billing_schedule_id?: string;
  waitlist_entry_id?: string;
  queue_position?: number;
  
  // Recovery tracking
  retry_count: number;
  failure_reasons: string[];
  recovery_actions: RecoveryAction[];
  
  // Rollback data
  rollback_data?: RollbackData;
}

export interface RecoveryAction {
  action_type: 'retry_payment' | 'backup_card' | 'payment_plan' | 'manual_intervention';
  attempted_at: Date;
  success: boolean;
  details: string;
}

export interface RollbackData {
  enrollment_deleted?: boolean;
  billing_schedule_deleted?: boolean;
  notifications_cancelled?: boolean;
  rollback_completed_at?: Date;
}

export interface TransactionResult {
  success: boolean;
  transaction: EnrollmentTransaction;
  message: string;
  enrollment_status: 'enrolled' | 'waitlisted' | 'failed';
}

export interface PaymentValidation {
  is_valid: boolean;
  can_use_auto_billing: boolean;
  validation_errors: string[];
  suggested_alternatives: string[];
}

/**
 * Ultra-Smart Enrollment Transaction Service
 * 
 * This service implements atomic transactions for enrollment+billing operations,
 * ensuring either ALL operations succeed or ALL are rolled back automatically.
 * No more revenue loss from partial successes!
 */
export class EnrollmentTransactionService {
  
  /**
   * Create a new enrollment transaction with validation and pre-checks
   */
  async createTransaction(request: EnrollmentTransactionRequest): Promise<EnrollmentTransaction> {
    const context: ErrorContext = {
      operation: 'createEnrollmentTransaction',
      additionalInfo: { request }
    };

    try {
      // PHASE 1: Pre-validate everything before creating transaction
      const validation = await this.validateTransactionRequest(request);
      if (!validation.is_valid) {
        throw new Error(`Transaction validation failed: ${validation.validation_errors.join(', ')}`);
      }

      // PHASE 2: Create transaction record with expiration
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + (request.payment_window_hours || 48));

      const transactionData = {
        student_id: request.student_id,
        class_id: request.class_id,
        organization_id: request.organization_id,
        enrolled_by: request.enrolled_by,
        
        status: 'pending' as const,
        expires_at: expiresAt.toISOString(),
        
        auto_billing_enabled: request.auto_billing_enabled,
        billing_amount_cents: request.billing_amount_cents || null,
        billing_day: request.billing_day || null,
        payment_strategy: request.payment_strategy || 'immediate',
        payment_window_hours: request.payment_window_hours || 48,
        
        retry_count: 0,
        failure_reasons: [],
        recovery_actions: []
      };

      const { data: transaction, error } = await supabase
        .from('enrollment_transactions')
        .insert(transactionData)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Created enrollment transaction:', transaction.id);
      return transaction as EnrollmentTransaction;

    } catch (error) {
      handleError(error, "Failed to create enrollment transaction", ErrorSeverity.ERROR, context);
      throw error;
    }
  }

  /**
   * Execute the atomic transaction with comprehensive rollback on failure
   */
  async executeTransaction(transactionId: string): Promise<TransactionResult> {
    const context: ErrorContext = {
      operation: 'executeEnrollmentTransaction',
      additionalInfo: { transactionId }
    };

    try {
      // PHASE 1: Get and validate transaction
      const transaction = await this.getTransaction(transactionId);
      if (!transaction) {
        throw new Error('Transaction not found');
      }

      if (transaction.status !== 'pending') {
        throw new Error(`Transaction already processed with status: ${transaction.status}`);
      }

      if (new Date() > new Date(transaction.expires_at)) {
        await this.expireTransaction(transactionId);
        throw new Error('Transaction expired');
      }

      // PHASE 2: Update status to processing
      await this.updateTransactionStatus(transactionId, 'processing');

      // PHASE 3: Execute atomic operations in sequence
      let enrollmentResult: any = null;
      let billingResult: any = null;
      let rollbackData: RollbackData = {};

      try {
        // Step 1: Attempt enrollment
        console.log(`🎯 Attempting enrollment for transaction ${transactionId}`);
        enrollmentResult = await this.executeEnrollment(transaction);
        
        if (!enrollmentResult.success) {
          throw new Error(enrollmentResult.message);
        }

        // Update transaction with enrollment results
        await this.updateTransactionResults(transactionId, {
          enrollment_id: enrollmentResult.enrollment_id,
          waitlist_entry_id: enrollmentResult.waitlist_entry_id,
          queue_position: enrollmentResult.queue_position
        });

        // Step 2: If enrollment successful and auto-billing enabled, attempt billing setup
        if (enrollmentResult.status === 'enrolled' && transaction.auto_billing_enabled) {
          console.log(`💳 Attempting auto-billing setup for transaction ${transactionId}`);
          billingResult = await this.executeBillingSetup(transaction, enrollmentResult.enrollment_id);
          
          if (!billingResult.success) {
            console.error('💥 Billing setup failed, initiating rollback');
            // CRITICAL: Billing failed after enrollment succeeded - must rollback
            await this.rollbackEnrollment(enrollmentResult.enrollment_id);
            rollbackData.enrollment_deleted = true;
            throw new Error(`Billing setup failed: ${billingResult.error}. Enrollment has been rolled back.`);
          }

          // Update transaction with billing results
          await this.updateTransactionResults(transactionId, {
            billing_schedule_id: billingResult.billing_schedule_id
          });
        }

        // Step 3: Send notifications (non-blocking)
        this.sendTransactionNotifications(transaction, enrollmentResult, billingResult).catch(error => {
          console.error('⚠️ Notification sending failed (non-blocking):', error);
        });

        // PHASE 4: Mark transaction as completed
        await this.updateTransactionStatus(transactionId, 'completed');

        const updatedTransaction = await this.getTransaction(transactionId);
        console.log(`✅ Transaction ${transactionId} completed successfully`);

        return {
          success: true,
          transaction: updatedTransaction!,
          message: this.generateSuccessMessage(enrollmentResult, billingResult),
          enrollment_status: enrollmentResult.status
        };

      } catch (atomicError) {
        // PHASE 5: Comprehensive rollback on any failure
        console.error(`💥 Atomic operation failed for transaction ${transactionId}:`, atomicError);
        
        try {
          await this.performComprehensiveRollback(transaction, rollbackData);
          await this.updateTransactionStatus(transactionId, 'rolled_back');
        } catch (rollbackError) {
          console.error('🚨 CRITICAL: Rollback failed:', rollbackError);
          await this.updateTransactionStatus(transactionId, 'failed');
        }

        throw atomicError;
      }

    } catch (error) {
      handleError(error, "Failed to execute enrollment transaction", ErrorSeverity.ERROR, context);
      
      const transaction = await this.getTransaction(transactionId);
      return {
        success: false,
        transaction: transaction!,
        message: error instanceof Error ? error.message : 'Unknown error',
        enrollment_status: 'failed'
      };
    }
  }

  /**
   * Validate transaction request with comprehensive pre-checks
   */
  private async validateTransactionRequest(request: EnrollmentTransactionRequest): Promise<PaymentValidation> {
    const validationErrors: string[] = [];
    const suggestedAlternatives: string[] = [];

    try {
      // Check 1: Student exists and is valid
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('id, family_id')
        .eq('id', request.student_id)
        .eq('organization_id', request.organization_id)
        .single();

      if (studentError || !student) {
        validationErrors.push('Student not found or not in this organization');
      }

      // Check 2: Class exists and has capacity
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('id, name, max_students, is_active')
        .eq('id', request.class_id)
        .eq('organization_id', request.organization_id)
        .single();

      if (classError || !classData) {
        validationErrors.push('Class not found or not in this organization');
      } else if (!classData.is_active) {
        validationErrors.push('Class is not currently active');
      }

      // Check 3: Student not already enrolled
      const { data: existingEnrollment } = await supabase
        .from('enrollments')
        .select('id')
        .eq('student_id', request.student_id)
        .eq('class_id', request.class_id)
        .eq('status', 'active')
        .single();

      if (existingEnrollment) {
        validationErrors.push('Student is already enrolled in this class');
      }

      // Check 4: Auto-billing validation if enabled
      let canUseAutoBilling = false;
      if (request.auto_billing_enabled) {
        // Validate billing amount
        if (!request.billing_amount_cents || request.billing_amount_cents <= 0) {
          validationErrors.push('Invalid billing amount for auto-billing');
        }

        // Validate billing day
        if (!request.billing_day || request.billing_day < 1 || request.billing_day > 28) {
          validationErrors.push('Invalid billing day (must be 1-28)');
        }

        // Check Stripe Connect status
        const { data: orgData } = await supabase
          .from('organizations')
          .select('billing_settings')
          .eq('id', request.organization_id)
          .single();

        const stripeAccountId = orgData?.billing_settings?.stripe_account_id;
        if (!stripeAccountId) {
          validationErrors.push('Auto-billing requires Stripe Connect setup');
          suggestedAlternatives.push('Complete Stripe Connect setup first');
        } else {
          canUseAutoBilling = true;
        }
      }

      return {
        is_valid: validationErrors.length === 0,
        can_use_auto_billing: canUseAutoBilling,
        validation_errors: validationErrors,
        suggested_alternatives: suggestedAlternatives
      };

    } catch (error) {
      console.error('Validation error:', error);
      return {
        is_valid: false,
        can_use_auto_billing: false,
        validation_errors: ['Validation check failed'],
        suggested_alternatives: ['Please try again']
      };
    }
  }

  /**
   * Execute enrollment with waitlist handling
   */
  private async executeEnrollment(transaction: EnrollmentTransaction): Promise<any> {
    const { data: enrollmentResult, error: enrollmentError } = await supabase
      .rpc('manage_enrollment_with_waitlist', {
        p_class_id: transaction.class_id,
        p_student_id: transaction.student_id,
        p_organization_id: transaction.organization_id,
        p_enrolled_by: transaction.enrolled_by
      });

    if (enrollmentError) throw enrollmentError;

    const result = enrollmentResult as {
      success: boolean;
      status: string;
      message: string;
      enrollment_id?: string;
      waitlist_entry_id?: string;
      queue_position?: number;
    };

    if (!result.success) {
      throw new Error(result.message);
    }

    return result;
  }

  /**
   * Execute billing setup with validation
   */
  private async executeBillingSetup(transaction: EnrollmentTransaction, enrollmentId: string): Promise<any> {
    if (!transaction.billing_amount_cents || !transaction.billing_day) {
      return { success: false, error: 'Missing billing configuration' };
    }

    // Calculate next billing date
    const today = new Date();
    const nextBillingDate = new Date(today.getFullYear(), today.getMonth(), transaction.billing_day);
    
    // If billing day has passed this month, move to next month
    if (nextBillingDate <= today) {
      nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    }

    const { data: billingSchedule, error: billingError } = await supabase
      .from('billing_schedules')
      .insert({
        enrollment_id: enrollmentId,
        organization_id: transaction.organization_id,
        amount_cents: transaction.billing_amount_cents,
        billing_day: transaction.billing_day,
        next_billing_date: nextBillingDate.toISOString().split('T')[0],
        status: 'active',
        description: `Monthly tuition for enrollment ${enrollmentId}`
      })
      .select()
      .single();

    if (billingError) {
      return { success: false, error: billingError.message };
    }

    return { 
      success: true, 
      billing_schedule_id: billingSchedule.id,
      next_billing_date: nextBillingDate 
    };
  }

  /**
   * Perform comprehensive rollback of all operations
   */
  private async performComprehensiveRollback(transaction: EnrollmentTransaction, rollbackData: RollbackData): Promise<void> {
    console.log(`🔄 Starting comprehensive rollback for transaction ${transaction.id}`);

    // Rollback billing schedule if created
    if (transaction.billing_schedule_id && !rollbackData.billing_schedule_deleted) {
      try {
        await supabase
          .from('billing_schedules')
          .delete()
          .eq('id', transaction.billing_schedule_id);
        
        rollbackData.billing_schedule_deleted = true;
        console.log(`✅ Rolled back billing schedule ${transaction.billing_schedule_id}`);
      } catch (error) {
        console.error('❌ Failed to rollback billing schedule:', error);
      }
    }

    // Rollback enrollment if created
    if (transaction.enrollment_id && !rollbackData.enrollment_deleted) {
      try {
        await this.rollbackEnrollment(transaction.enrollment_id);
        rollbackData.enrollment_deleted = true;
        console.log(`✅ Rolled back enrollment ${transaction.enrollment_id}`);
      } catch (error) {
        console.error('❌ Failed to rollback enrollment:', error);
      }
    }

    // Update transaction with rollback data
    await supabase
      .from('enrollment_transactions')
      .update({ 
        rollback_data: rollbackData,
        updated_at: new Date().toISOString()
      })
      .eq('id', transaction.id);

    console.log(`✅ Comprehensive rollback completed for transaction ${transaction.id}`);
  }

  /**
   * Rollback enrollment and handle waitlist
   */
  private async rollbackEnrollment(enrollmentId: string): Promise<void> {
    await supabase
      .from('enrollments')
      .delete()
      .eq('id', enrollmentId);
  }

  /**
   * Send transaction notifications (non-blocking)
   */
  private async sendTransactionNotifications(
    transaction: EnrollmentTransaction, 
    enrollmentResult: any, 
    billingResult: any
  ): Promise<void> {
    // Implementation for sending notifications to families, admins, etc.
    // This is non-blocking and failures don't affect transaction success
    console.log(`📧 Sending notifications for transaction ${transaction.id}`);
  }

  /**
   * Generate success message based on results
   */
  private generateSuccessMessage(enrollmentResult: any, billingResult: any): string {
    if (enrollmentResult.status === 'waitlisted') {
      return `Student added to waitlist at position ${enrollmentResult.queue_position}`;
    }

    if (billingResult?.success) {
      return 'Student enrolled successfully with auto-billing activated';
    }

    return 'Student enrolled successfully';
  }

  // =====================================================
  // UTILITY METHODS
  // =====================================================

  private async getTransaction(transactionId: string): Promise<EnrollmentTransaction | null> {
    const { data, error } = await supabase
      .from('enrollment_transactions')
      .select('*')
      .eq('id', transactionId)
      .single();

    if (error) return null;
    return data as EnrollmentTransaction;
  }

  private async updateTransactionStatus(transactionId: string, status: EnrollmentTransaction['status']): Promise<void> {
    await supabase
      .from('enrollment_transactions')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', transactionId);
  }

  private async updateTransactionResults(transactionId: string, results: Partial<EnrollmentTransaction>): Promise<void> {
    await supabase
      .from('enrollment_transactions')
      .update({ 
        ...results,
        updated_at: new Date().toISOString()
      })
      .eq('id', transactionId);
  }

  private async expireTransaction(transactionId: string): Promise<void> {
    await this.updateTransactionStatus(transactionId, 'expired');
  }
}

// Singleton instance
export const enrollmentTransactionService = new EnrollmentTransactionService();